import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
    AllupBetlineFragment,
    BadgesIconQueryResult,
    ContentItemFragment,
    KolDataDetailQueryResult,
    KolProfileFragment,
    MetaFragment,
    NewsFragment,
} from '@graphqlTypes/sitecore';
import { AppLanguage } from '@src/common/app-language';
import { sitecoreClient, withOps } from '@src/utils/graphql-request';
import cn from 'classnames';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useKolStoreSetup } from '@src/hooks/use-kol-store-setup';
import { useGlobalStore } from '@src/hooks/use-global-store';
import BetPickList from '@components/kol/bet-pick-list';
import { observer } from 'mobx-react-lite';
import { withBasePath } from '@src/common/path-convert';
import { isTimeFrame, objFilter } from '@src/utils/util';
import VideoIcon from '@public/images/kol/Play.svg';
import { useTranslation } from 'react-i18next';
import { Resource } from 'i18next';
import BadgeDescription from '@components/badge-description/BadgeDescription';
import { format, isSameDay } from 'date-fns';
import { GetRacingDataQuery } from '@src/hooks/GetRacingDataQuery';
import OddsChanges from '@components/odds-changes/OddsChanges';
import OddsStore from '@mobx/stores/kolBetpick/kolOddsLastDate';
import AllBettingStrategyBTN from '@components/all-betting-strategy-BTN/AllBettingStrategyBTN';
import Open3in1App from '@components/open-3in1-app/Open3in1App';
import isSharePageStore from '@mobx/stores/isShare-store';
import bettingStrategyStore from '@mobx/stores/bettingStrategy-store';
import { reaction } from 'mobx';
import { toZonedTime } from 'date-fns-tz';
import useWindowSize from '@src/utils/useWindowSize';
import { mediaDomain } from '@src/middleware';
import Head from 'next/head';

interface Props {
    kolData: KolDataDetailQueryResult;
    badgesIconData: BadgesIconQueryResult;
    params: string;
    currentPath: string;
    metaItem: { meat: MetaFragment };
    mediaDomain: string;
    AllUpData: AllupBetlineFragment[];
    _nextI18Next: { initialI18nStore: Resource; initialLocale: string };
}
const Profile = (props: Props) => {
    // console.log('props-Profile-------', props.kolData);
    const { kolData, currentPath, AllUpData } = props;
    const contentRef = useRef<HTMLDivElement | null>(null);

    useKolStoreSetup();
    const { t, i18n } = useTranslation(['common', 'kol', 'share']);
    const { profilePageStore } = useGlobalStore();
    const refHeight = contentRef?.current?.offsetHeight || 0;
    const { height } = useWindowSize();
    const lang = i18n.language || 'en-us';
    const isEn = lang.toLocaleLowerCase() === 'en-us';

    /** 是否显示All Betting Strategy 按钮 */
    const [isShowBettingBtn, setIsShowBettingBtn] = useState(
        bettingStrategyStore.GetbettingBTNList()
    );

    const [kolProfilesItem, setKolProfilesItem] = useState<KolProfileFragment>();
    const [contentList, setContentList] = useState<NewsFragment[]>([]);
    const [titleHtml, setTitleHtml] = useState('');
    // const [badgesIconList, setBadgesIconList] = useState<
    //     Array<{ badgeIconLink?: { targetItem?: BadgesIconListFragment } }>
    // >([]);
    const [racesList, setRacesList] = useState([]);
    const [pBNum, setPBNum] = useState(0);
    const [styleCss, setStyleCss] = useState({});
    const [onceHalf, setOnceHalf] = useState(true);

    /** 不是CMS 填入查詢的日期，未來日期或者過去日期
     * true- 同一天數據，可以請求
     * false- 非同一天，不可以請求
     */
    // const [noQueryRace, setNoQueryRace] = useState(false);

    const isLastUpdate = profilePageStore?.betpicks && profilePageStore?.betpicks?.length > 0;

    const displayOdds =
        profilePageStore?.betpicks &&
        profilePageStore?.betpicks?.length > 0 &&
        profilePageStore?.betpicks[0].raceDay?.displayOdds?.boolValue;

    const displayManualInput =
        profilePageStore?.betpicks &&
        profilePageStore?.betpicks?.length > 0 &&
        profilePageStore?.betpicks[0].raceDay?.displayManualInput?.boolValue;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const imgUrl = (getUrl: string) => {
        if (getUrl.startsWith('//')) {
            return `https:${getUrl}`;
        } else if (getUrl.startsWith('/')) {
            return `${props.mediaDomain}${getUrl}`;
        } else {
            return getUrl;
        }
    };

    const { titleHtmlH, imgHtml, descriptionHtml } = useMemo(() => {
        let titleHtmlH = '';
        let imgHtml = '';
        let descriptionHtml = '';
        const defMeta = isEn ? 'KOL Profile' : 'KOL簡介';
        titleHtmlH = kolProfilesItem?.kolName?.value
            ? `${defMeta} - ${kolProfilesItem?.kolName?.value || ''} - ${
                  props.metaItem.meat?.metaTitleSuffix?.value
              } `
            : `${defMeta} - ${props.metaItem.meat?.metaTitleSuffix?.value}`;

        imgHtml = imgUrl(kolProfilesItem?.kolThumbRect?.src || '');
        descriptionHtml = kolProfilesItem?.kolDescription?.value || '';
        return { titleHtmlH, imgHtml, descriptionHtml };
    }, [
        imgUrl,
        isEn,
        kolProfilesItem?.kolDescription?.value,
        kolProfilesItem?.kolName?.value,
        kolProfilesItem?.kolThumbRect?.src,
        props.metaItem.meat?.metaTitleSuffix?.value,
    ]);

    /**
     * OddsChanges显示规则
     * displayOdds: true
     * displayManualInput: false
     */
    const showOddsChanges = displayOdds && !displayManualInput;

    const capitalizeWords = (str: string) => {
        return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase());
    };

    const getRacingData = async () => {
        const betpick = profilePageStore?.betpicks ?? [];

        const hongKongTime = toZonedTime(
            new Date(betpick[0]?.raceDay?.raceDayDate?.dateValue ?? 0),
            'Asia/Hong_Kong'
        );
        const raceDay = format(hongKongTime, 'yyyy-MM-dd');

        try {
            const racingResult = await GetRacingDataQuery(
                raceDay,
                betpick[0]?.raceDay?.venueCode?.value ?? ''
            );
            //    判斷是否同一天
            const parsedDate1 = raceDay;
            const parsedDate2 = racingResult[0].date;
            const isSameDayResult = isSameDay(parsedDate1, parsedDate2);
            const isSameVenueCode =
                betpick[0]?.raceDay?.venueCode?.value === racingResult[0].venueCode;
            // console.log('isSameVenueCode---', isSameVenueCode);
            // setNoQueryRace(isSameDayResult && isSameVenueCode);
            if (!isSameDayResult || !isSameVenueCode) {
                bettingStrategyStore.setResetList();
                setRacesList([]);
                return;
            }

            const data = racingResult.length > 0 ? racingResult[0].races ?? [] : [];
            setRacesList(data);
        } catch (error) {
            console.error('Error fetching odds data:', error);
        }
    };

    // 获取odds和racing数据
    const queryGQL = async () => {
        await getRacingData();
        // await getOddsData();
    };

    useEffect(() => {
        if (refHeight > 0 && onceHalf) {
            console.log('**********halfStyle*******', refHeight);
            const timeOutId = setTimeout(() => {
                const halfH = refHeight / 2;
                const curMinHeight = height - pBNum;

                const conditionalStyle = {
                    height: `${halfH > curMinHeight ? halfH + pBNum : curMinHeight}px`,
                    overflow: 'hidden',
                    marginBottom: `${pBNum}px`,
                };

                setStyleCss(conditionalStyle);
                setOnceHalf(false);
            }, 800);

            return () => clearTimeout(timeOutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refHeight, pBNum]);

    useEffect(() => {
        const hasBetPicks = profilePageStore.betpicks && profilePageStore.betpicks.length > 0;

        if (displayManualInput) {
            bettingStrategyStore.setResetBetpickTitleList();
            return;
        }
        if (hasBetPicks) {
            bettingStrategyStore.setResetBetpickTitleList();
            queryGQL();
        }

        const interval = setInterval(() => {
            if (hasBetPicks) {
                queryGQL(); // Call every 30 seconds if there are bet picks
            }
        }, 30000);

        return () => clearInterval(interval); // Clear the interval on unmount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profilePageStore.betpicks, refHeight, onceHalf]);

    useEffect(() => {
        const kolItem: KolProfileFragment | any = kolData.kolItem;

        setTitleHtml(kolItem.raceDayLink?.targetItem?.raceDayInfoHTML?.rendered || '');

        const getContentList = () => {
            const newContent: ContentItemFragment[] =
                (kolItem.news?.length > 0 && objFilter(kolItem.news)) || [];
            if (newContent.length > 0 && newContent[0].content.length > 0) {
                return newContent[0].content.filter(
                    (i) =>
                        isTimeFrame(
                            i.isPublished?.boolValue || false,
                            i.startDatetime?.dateValue || 0,
                            i.endDatetime?.dateValue || 0
                        ) &&
                        i.heroImageSq?.src &&
                        i.pageTitle?.value
                );
            } else {
                return [];
            }
        };

        setKolProfilesItem(kolItem);
        setContentList(getContentList);
    }, [kolData.kolItem]);

    // useEffect(() => {
    //     // eslint-disable-next-line @typescript-eslint/no-explicit-any
    //     const filteredBadges: any[] =
    //         (kolProfilesItem?.badges &&
    //             kolProfilesItem?.badges.length > 0 &&
    //             kolProfilesItem?.badges?.flatMap((badge) =>
    //                 badge.children.filter(
    //                     (child) => child.badgeIconLink && child.badgeIconLink.targetItem
    //                 )
    //             )) ||
    //         [];
    //     setBadgesIconList(filteredBadges);
    // }, [kolProfilesItem]);

    useEffect(() => {
        isSharePageStore.setIsSharePage(true);
        document.documentElement.style.backgroundColor = 'white';
    }, []);

    useEffect(() => {
        OddsStore.addLastUpdateDate([]);
    }, []);

    useEffect(() => {
        const dispose = reaction(
            () => bettingStrategyStore.GetbettingBTNList(),
            (showButton) => {
                setIsShowBettingBtn(showButton);
            }
        );

        return () => dispose();
    }, []);

    return (
        <div style={{ width: '100%', background: 'white' }}>
            <Head>
                <title>{titleHtmlH}</title>
                <meta property='og:title' content={titleHtmlH} key='title' />
                <meta property='og:image' content={imgHtml} />
                <meta property='og:description' content={descriptionHtml} />
            </Head>

            <div
                className={`${!!isLastUpdate && 'pb-6'} pointer-events-none overflow-hidden`}
                style={{
                    maxWidth: '1440px',
                    margin: '0 auto',
                    position: 'relative',
                    background: '#F0F0F0',
                    ...styleCss,
                }}
                ref={contentRef}
            >
                <div
                    className={cn(
                        'bg-cover bg-center bg-no-repeat w-full h-[153px]  blur-lg backdrop-blur-xl absolute top-0 overflow-hidden'
                    )}
                    style={{
                        backgroundImage: `url(
                            ${kolProfilesItem?.kolBg ? kolProfilesItem?.kolBg?.src : ''}
                        )`,
                    }}
                />

                <img
                    className='w-[24px] h-[24px] absolute top-[15px] right-[15px] z-10'
                    src={withBasePath('/images/kol/share-blue.svg')}
                    alt=''
                    loading='lazy'
                />

                <div className={cn(`pl-[17px] pr-[17px] min-h-[100vh]`)}>
                    {/* header */}
                    <div className='flex flex-col items-center mt-[23px] mb-[13px] '>
                        <div className='relative z-20'>
                            <img
                                className='rounded-[100%] overflow-hidden z-20 w-[96px] h-[96px] object-cover'
                                src={kolProfilesItem?.kolThumbSq?.src}
                                alt=''
                                loading='lazy'
                            />
                            {kolProfilesItem?.profileVideo?.value && (
                                <VideoIcon className='absolute bottom-[-8px] right-[-3px] scale-150 w-[22px] h-[22px]'></VideoIcon>
                            )}
                        </div>

                        <div
                            className={cn(
                                `bg-[#fff] w-[100%] rounded-[4px] pb-[14px] px-[16px] text-center mt-[-42px] z-10  min-h-[180px]`
                            )}
                        >
                            <div className='pt-[51px] pb-[9px]'>
                                <div
                                    className='text-[18px] font-medium'
                                    dangerouslySetInnerHTML={{
                                        __html: kolProfilesItem?.kolName?.value || '',
                                    }}
                                />
                                <div className='inline-flex justify-center items-center gap-2'>
                                    {Number(kolProfilesItem?.monthChampionCount?.value) > 0 && (
                                        <div className='text-[#003C84] text-[11px] font-bold flex justify-center items-center'>
                                            <img
                                                src={withBasePath('/images/kol/i-crown.svg')}
                                                alt=''
                                                className='w-4 h-4 mr-1'
                                                loading='lazy'
                                            />
                                            <div className='whitespace-nowrap'>
                                                {kolProfilesItem?.monthChampionCount?.value || 0}
                                            </div>
                                        </div>
                                    )}
                                    {Number(kolProfilesItem?.highestStrikeRateCount?.value) > 0 && (
                                        <div className='text-[#003C84] text-[11px] font-bold flex justify-center items-center'>
                                            <img
                                                src={withBasePath('/images/kol/i-crown2.svg')}
                                                alt=''
                                                className='w-4 h-4 mr-1'
                                                loading='lazy'
                                            />
                                            <div className='whitespace-nowrap'>
                                                {kolProfilesItem?.highestStrikeRateCount?.value ||
                                                    0}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={cn(
                                        'font-medium text-[16px] text-black pt-[2px]'
                                        // textBreakWords && 'break-words'
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            kolProfilesItem?.leaderboardDescription?.rendered || '',
                                    }}
                                ></div>
                            </div>
                            {/* <div>
                                {badgesIconList.length > 0 && badgesIconList.length > 1 && (
                                    <div>
                                        <div
                                            className='text-[18px] pt-[51px] pb-[3px]'
                                            dangerouslySetInnerHTML={{
                                                __html: kolProfilesItem?.kolName?.value || '',
                                            }}
                                        />
                                        <div>
                                            {badgesIconList.map((i, index) => (
                                                <img
                                                    className='w-[14px] h-[14px] mr-[2px]'
                                                    src={
                                                        i.badgeIconLink?.targetItem?.badgeIconImage
                                                            ?.src || ''
                                                    }
                                                    loading='lazy'
                                                    alt=''
                                                    key={index}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {badgesIconList.length === 1 && (
                                    <div className='flex justify-center items-baseline'>
                                        <div
                                            className='text-[18px] pt-[51px] pb-[3px] pr-[4px]'
                                            dangerouslySetInnerHTML={{
                                                __html: kolProfilesItem?.kolName?.value || '',
                                            }}
                                        />
                                        <div>
                                            {badgesIconList.map((i, index) => (
                                                <img
                                                    className='w-[14px] h-[14px]'
                                                    src={
                                                        i.badgeIconLink?.targetItem?.badgeIconImage
                                                            ?.src || ''
                                                    }
                                                    loading='lazy'
                                                    alt=''
                                                    key={index}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {badgesIconList.length < 1 && (
                                    <div
                                        className='text-[18px] pt-[51px] pb-[9px]'
                                        dangerouslySetInnerHTML={{
                                            __html: kolProfilesItem?.kolName?.value || '',
                                        }}
                                    />
                                )}
                            </div> */}

                            <div>
                                <div
                                    className='text-[15px] text-center'
                                    dangerouslySetInnerHTML={{
                                        __html: kolProfilesItem?.kolDescription?.value || '',
                                    }}
                                />
                                <div className='flex justify-around pt-[12px] mt-[12px] border-t border-[#d5d5d5]'>
                                    <div className='flex flex-col'>
                                        <div className='font-bold text-[13px]'>
                                            {t('kol:profile.rank')}
                                        </div>
                                        <div className='flex justify-center items-center'>
                                            {kolProfilesItem?.trendIIcon?.value &&
                                                kolProfilesItem?.trendIIcon?.value?.length > 0 && (
                                                    <img
                                                        className='w-[8px] mr-[5px]'
                                                        src={withBasePath(
                                                            `${
                                                                kolProfilesItem?.trendIIcon
                                                                    ?.value === 'Up'
                                                                    ? '/images/kol/up.svg'
                                                                    : '/images/kol/down.svg'
                                                            }`
                                                        )}
                                                        loading='lazy'
                                                        alt=''
                                                    />
                                                )}

                                            <div className='font-bold text-[16px] text-[#012E65]'>
                                                {kolProfilesItem?.rank?.value}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex flex-col'>
                                        <div className='font-bold text-[13px]'>
                                            {t('kol:profile.strikeRate')}
                                        </div>
                                        <div className='font-bold text-[16px] text-[#012E65]'>
                                            {kolProfilesItem?.strikeRate?.value}
                                        </div>
                                    </div>
                                    <div className='flex flex-col'>
                                        <div className='font-bold text-[13px]'>
                                            {t('kol:profile.score')}
                                        </div>
                                        <div className='font-bold text-[16px] text-[#012E65]'>
                                            {`${kolProfilesItem?.score?.value}${t(
                                                'kol:betpick.pts'
                                            )}`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {kolProfilesItem?.raceDayBetEnabled?.boolValue && (
                        <div>
                            <div className=''>
                                <div dangerouslySetInnerHTML={{ __html: titleHtml }}></div>
                            </div>

                            {bettingStrategyStore.GetBetpickTitleList() && (
                                <div className='flex justify-between items-center py-[12.5px]'>
                                    <div className='font-medium text-[18px]'>
                                        {t('kol:betlinesOverview')}
                                    </div>

                                    {isShowBettingBtn && (
                                        <AllBettingStrategyBTN contentBlock={kolProfilesItem} />
                                    )}
                                </div>
                            )}

                            <BetPickList
                                type='race'
                                betPicks={profilePageStore.betpicks}
                                racesList={racesList}
                                isKolProfile={true}
                                AllUpData={AllUpData}
                            />
                        </div>
                    )}

                    {kolProfilesItem?.raceDayBetEnabled?.boolValue &&
                        profilePageStore.betpicks &&
                        profilePageStore.betpicks.length > 0 && (
                            <>
                                <div className='pt-[10px] flex justify-between items-baseline'>
                                    <BadgeDescription
                                        badgeIconData={props.badgesIconData.Item?.data || []}
                                    ></BadgeDescription>

                                    <div className='text-right mb-[24px] flex-1'>
                                        <a className='text-[#0234A6] text-[13px] font-bold'>
                                            {t('kol:profile.kolGameRules')}
                                            <div className='ml-1 align-middle inline-block rounded-full border-2 w-[15px] h-[15px] border-[#0234A6] font-bold text-[10px] leading-[11px] text-center'>
                                                ?
                                            </div>
                                        </a>
                                    </div>
                                </div>
                                {showOddsChanges && racesList.length > 0 && <OddsChanges />}
                            </>
                        )}

                    {/* footer */}
                    {contentList.length > 0 && (
                        <div>
                            <div
                                className='text-[15px] text-black pb-[12px]'
                                dangerouslySetInnerHTML={{
                                    __html:
                                        props._nextI18Next.initialLocale === 'zh-HK'
                                            ? `想了解${capitalizeWords(props.params)}更多`
                                            : `More about ${capitalizeWords(props.params)}`,
                                }}
                            ></div>
                            <div className='overflow-y-auto'>
                                {contentList.length > 0 &&
                                    contentList.map((i: NewsFragment, index: number) => {
                                        const isDeepLink = i.blockType?.value === 'DeepLink';
                                        return (
                                            <div key={index}>
                                                {isDeepLink ? (
                                                    <div className='relative pointer-events-none'>
                                                        {i.shareIcon?.boolValue && (
                                                            <img
                                                                className='w-[24px] h-[24px] absolute top-[8px] right-[8px] z-10'
                                                                src={withBasePath(
                                                                    '/images/kol/share-blue.svg'
                                                                )}
                                                                loading='lazy'
                                                                alt=''
                                                            />
                                                        )}
                                                        <a href={''} target='_blank'>
                                                            <div className='h-[120px] rounded-[4px] bg-[#fff] p-[10px] relative flex mb-[10px]'>
                                                                <div>
                                                                    {isTimeFrame(
                                                                        (i.isIconDisplay &&
                                                                            i.isIconDisplay
                                                                                .boolValue) ||
                                                                            false,
                                                                        i.iconDisplayStartDate
                                                                            ?.dateValue || 0,
                                                                        i.iconDisplayEndDate
                                                                            ?.dateValue || 0
                                                                    ) && (
                                                                        <img
                                                                            src={
                                                                                i.iconDisplay
                                                                                    ?.targetItem
                                                                                    ?.iconImage?.src
                                                                            }
                                                                            loading='lazy'
                                                                            alt=''
                                                                            className='h-[20px] absolute top-[15px] left-[15px]'
                                                                        ></img>
                                                                    )}
                                                                    <img
                                                                        className='w-[100px] h-[100px] rounded-[4px] mr-[12px] object-cover'
                                                                        src={i?.heroImageSq?.src}
                                                                        alt=''
                                                                        loading='lazy'
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col flex-1 justify-center'>
                                                                    <div
                                                                        className='text-[11px] mb-[10px]  text-[#031E6C] categoryBr line-clamp-1'
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: `${
                                                                                (i?.category
                                                                                    ?.categories &&
                                                                                    i.category
                                                                                        .categories
                                                                                        .length >
                                                                                        0 &&
                                                                                    objFilter(
                                                                                        i?.category
                                                                                            ?.categories
                                                                                    )[0]
                                                                                        ?.categoryTitle
                                                                                        ?.value) ||
                                                                                ''
                                                                            }`,
                                                                        }}
                                                                    ></div>
                                                                    <div
                                                                        className='text-[13px] line-clamp-3'
                                                                        dangerouslySetInnerHTML={{
                                                                            __html:
                                                                                i.pageTitle
                                                                                    ?.value || '',
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    </div>
                                                ) : (
                                                    <div className='h-[120px] rounded-[4px] bg-[#fff] p-[10px] relative flex mb-[10px]'>
                                                        {i.shareIcon?.boolValue && (
                                                            <img
                                                                className='w-[24px] h-[24px] absolute top-[8px] right-[8px] z-10'
                                                                src={withBasePath(
                                                                    '/images/kol/share-blue.svg'
                                                                )}
                                                                alt=''
                                                                loading='lazy'
                                                            />
                                                        )}

                                                        <div>
                                                            {isTimeFrame(
                                                                (i.isIconDisplay &&
                                                                    i.isIconDisplay.boolValue) ||
                                                                    false,
                                                                i.iconDisplayStartDate?.dateValue ||
                                                                    0,
                                                                i.iconDisplayEndDate?.dateValue || 0
                                                            ) && (
                                                                <img
                                                                    src={
                                                                        i.iconDisplay?.targetItem
                                                                            ?.iconImage?.src
                                                                    }
                                                                    alt=''
                                                                    loading='lazy'
                                                                    className='h-[20px] absolute top-[15px] left-[15px]'
                                                                ></img>
                                                            )}
                                                            <img
                                                                className='w-[100px] h-[100px] rounded-[4px] mr-[12px] object-cover'
                                                                src={i?.heroImageSq?.src}
                                                                alt=''
                                                                loading='lazy'
                                                            />
                                                        </div>
                                                        <div className='flex flex-col flex-1 justify-center'>
                                                            <div
                                                                className='text-[11px] mb-[10px]  text-[#031E6C] categoryBr line-clamp-1'
                                                                dangerouslySetInnerHTML={{
                                                                    __html: `${
                                                                        (i?.category?.categories &&
                                                                            i.category.categories
                                                                                .length > 0 &&
                                                                            objFilter(
                                                                                i?.category
                                                                                    ?.categories
                                                                            )[0]?.categoryTitle
                                                                                ?.value) ||
                                                                        ''
                                                                    }`,
                                                                }}
                                                            ></div>
                                                            <div
                                                                className='text-[13px] line-clamp-3'
                                                                dangerouslySetInnerHTML={{
                                                                    __html:
                                                                        i.pageTitle?.value || '',
                                                                }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    )}

                    <div className='text-[#333] text-[11px] font-normal bg-[#f4f4f4] pb-[20px]'>
                        {t('kol:profile.Remark')}
                    </div>
                </div>

                <Open3in1App
                    currentPath={currentPath}
                    contentBlock={kolProfilesItem}
                    getOverLayHeight={(v) => setPBNum(v)}
                />
            </div>
        </div>
    );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const params = ctx.params ? ctx.params.leader : '';
    const kolData = await withOps('share-kol-Profile-contentBlockData', () =>
        sitecoreClient.kolDataDetailQuery({
            path: `/sitecore/content/Sites/Discover/kol/${params}`,
            lang: ctx.locale || AppLanguage.en_us,
        })
    );

    const currentProfile = kolData?.kolItem;

    const badgesIconData = await withOps('share-kol-Profile-badgesIconData', () =>
        sitecoreClient.BadgesIconQuery({
            lang: ctx.locale || AppLanguage.en_us,
        })
    );

    const metaItem = await withOps('share-kol-Profile-metaItem', () =>
        sitecoreClient.metaQuery({ lang: ctx.locale || '' })
    );

    const AllUpData = await withOps('share-kol-Profile-AllUpData', () =>
        sitecoreClient.AllupBetlineQuery({
            lang: ctx.locale || AppLanguage.en_us,
        })
    );

    return {
        props: {
            kolData,
            badgesIconData,
            params: ctx.params ? ctx.params.leader : '',
            sectionData: currentProfile ? { Item: currentProfile } : undefined,
            pageType: 'article',
            currentPath: `kol-profile/${ctx.params ? ctx.params.leader || '' : ''}`,
            metaItem,
            mediaDomain,
            AllUpData: AllUpData.allupBetlines?.data,
            ...(await serverSideTranslations(ctx.locale || AppLanguage.en_us, [
                'common',
                'kol',
                'share',
            ])),
        },
    };
};

export default observer(Profile);
